import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom'; // Assuming you're using react-router
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import LoadingButton from '@mui/lab/LoadingButton';
import { getGroqChatCompletion } from '../../utils/groqUtils'; // Ensure this is properly imported
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';

const AnalyzePage = () => {
  const { id } = useParams(); // Get the id from the URL params
  const [inputMessage, setInputMessage] = useState('');
  const [chatResponse, setChatResponse] = useState<string[]>([]);
  const [userMessages, setUserMessages] = useState<string[]>([]);
  const [data, setData] = useState<(string | number | boolean | null)[][]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestionsVisible, setSuggestionsVisible] = useState(true);

  const chatEndRef = useRef<HTMLDivElement>(null); // Create a ref for the chat container

  const chatSuggestions = [
    'Can you help me analyze this data?',
    'What are the key trends in this dataset?',
    'Can you identify any outliers in the data?',
    'What insights can be derived from this table?',
  ];

  type DataRow = (string | number)[];

  interface ExcelData {
    fileName: string;
    data: DataRow[];
  }

  interface RootState {
    data: Record<string, ExcelData>;
  }

  const excelData = useSelector((state: RootState) => (id ? state.data[id] : undefined));

  useEffect(() => {
    const storedData = localStorage.getItem(`excelData_${id}`);
    console.log('Stored data:', storedData);
    
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        console.log('Parsed Stored Data:', parsedData);

        if (parsedData && typeof parsedData === 'object' && 'data' in parsedData) {
          const tableData = parsedData.data;
          console.log('Table Data:', tableData);

          if (Array.isArray(tableData) && tableData.length > 0) {
            setData(tableData);
          } else {
            console.log('No valid data array found in parsedData:', parsedData);
            setChatResponse(['No data found for analysis.']);
          }
        } else {
          console.log('Parsed data is not in expected format:', parsedData);
          setChatResponse(['No data found for analysis.']);
        }
      } catch (error) {
        console.error('Error parsing storedData:', error);
        setChatResponse(['Failed to parse data.']);
      }
    } else {
      setChatResponse(['No data found for analysis.']);
    }

    const storedMessages = localStorage.getItem(`userMessages_${id}`);
    const storedResponses = localStorage.getItem(`chatResponse_${id}`);
    const storedSuggestionsVisible = localStorage.getItem(`suggestionsVisible_${id}`);

    if (storedMessages) {
      setUserMessages(JSON.parse(storedMessages));
    }

    if (storedResponses) {
      setChatResponse(JSON.parse(storedResponses));
    }

    if (storedSuggestionsVisible !== null) {
      setSuggestionsVisible(JSON.parse(storedSuggestionsVisible));
    }

    setIsLoading(false);
  }, [id]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' }); // Scroll to the end smoothly
    }
  }, [chatResponse, userMessages]); // Trigger scroll when these states change

  const formatTableData = () => {
    return data
      .map(row => row.join(' | '))
      .join('\n');
  };

  const handleSubmit = async (message: string) => {
    if (message.trim()) {
      const updatedUserMessages = [...userMessages, message];
      setUserMessages(updatedUserMessages);
      setIsSubmitting(true);
      setSuggestionsVisible(false);

      const tableDataString = formatTableData();
      const fullMessage = `This is a table of data:\n${tableDataString}\n\n${message}`;

      try {
        const response = await getGroqChatCompletion(fullMessage);
        const updatedChatResponse = [...chatResponse, response];
        setChatResponse(updatedChatResponse);

        localStorage.setItem(`userMessages_${id}`, JSON.stringify(updatedUserMessages));
        localStorage.setItem(`chatResponse_${id}`, JSON.stringify(updatedChatResponse));
        localStorage.setItem(`suggestionsVisible_${id}`, JSON.stringify(suggestionsVisible));
      } catch (error) {
        console.error('Error in handleSubmit:', error);
        setError('Seems like an error occurred please check your internet and try again!.');
      } finally {
        setIsSubmitting(false);
      }
      setInputMessage(''); // Clear input after sending
    }
  };

  return (
    <div className='transparent-background px-8 pt-8 rounded-xl h-[80vh] flex flex-col overflow-scroll'>
      {isLoading ? (
        <Box sx={{ width: '100%' }}>
          <Skeleton />
          <Skeleton animation="wave" />
          <Skeleton animation="wave" />
        </Box>
      ) : (
        <div className='flex flex-col justify-between h-[100%]'>
          <div className='w-full flex justify-center items-center flex-col'>
            <div className='mt-8 w-[80%]'>
              {userMessages.map((message, index) => (
                <div key={index}>
                  <div className='gradient-background'>
                    <p className='text-black p-3 font-medium text-sm rounded h-fit mt-2 border border-[white]'>
                      {message}
                    </p>
                  </div>
                  <div className='w-full flex mt-8 bg-[#29292990] text-[#fff] p-3 rounded'>
                    {chatResponse[index] && (
                      <div className=''>
                        <p className='text-sm'>
                          {chatResponse[index].split('\n').map((line, i) => (
                            <React.Fragment key={i}>
                              {line.includes('**') ? (
                                <p className="font-normal">{line.replace(/\*\*/g, '')}</p>
                              ) : (
                                <span>{line}</span>
                              )}
                              <br />
                            </React.Fragment>
                          ))}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} /> {/* Add a div at the end to scroll into view */}
            </div>
          </div>

          {suggestionsVisible && (
            <div className=''>
              <p className='text font-semibold text-xl text-center'>Suggested Questions:</p>
              <div className='gap-2 grid grid-cols-4 justify-center items-center mt-8'>
                {chatSuggestions.map((suggestion, index) => (
                  <div key={index}>
                    <button
                      onClick={() => handleSubmit(suggestion)}
                      className='text-semiold gradient-background h-20 px-8 rounded'
                    >
                      {suggestion}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className='bottom-0 sticky'>
            <div className='transparent-background p-2 rounded-sm'>
              {error && <p className='text-[red]'>{error}</p>}
              <input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type your message here..."
                className='chat bg-[white] px-3'
              />
              <button
                onClick={() => handleSubmit(inputMessage)}
                className='gradient-background px-4 mt-2 rounded-md p-1'
              >
                Submit data
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyzePage;
